import type {
  EntityClass,
  EntityType,
  KeyByComponent,
  Vector2,
} from '../../odie';
import {
  createEntity,
  DefineEntity,
  Entity2D,
  randomItem,
  View2DSystem,
} from '../../odie';
import { Container } from '../../odie';

import { AndroidBlockComponent } from '../components/AndroidBlockComponent';
import { AnimalBlockComponent } from '../components/AnimalBlockComponent';
import { BasicBlockComponent } from '../components/BasicBlockComponent';
import { BlockComponent } from '../components/BlockComponent';
import { BombBlockComponent } from '../components/BombBlockComponent';
import { ButterflyBlockComponent } from '../components/ButterflyBlockComponent';
import { CageBlockComponent } from '../components/CageBlockComponent';
import { CandleBlockComponent } from '../components/CandleBlockComponent';
import { PondBlockComponent } from '../components/PondBlockComponent';
import { CandyBagBlockComponent } from '../components/CandyBagBlockComponent';
import { CandyBlockComponent } from '../components/CandyBlockComponent';
import { ChainBlockComponent } from '../components/ChainBlockComponent';
import { ChameleonBlockComponent } from '../components/ChameleonBlockComponent';
import { CocoonBlockComponent } from '../components/CocoonBlockComponent';
import { CoffinBlockComponent } from '../components/CoffinBlockComponent';
import { CubeBlockComponent } from '../components/CubeBlockComponent';
import { CurtainBlockComponent } from '../components/CurtainBlockComponent';
import { DogBlockComponent } from '../components/DogBlockComponent';
import { DoorBlockComponent } from '../components/DoorBlockComponent';
import { FireboxBlockComponent } from '../components/FireboxBlockComponent';
import { FuguBlockComponent } from '../components/FuguBlockComponent';
import { FullIceBlockComponent } from '../components/FullIceBlockComponent';
import { GlowComponent } from '../components/GlowComponent';
import { HalfIceBlockComponent } from '../components/HalfIceBlockComponent';
import { HatBlockComponent } from '../components/HatBlockComponent';
import { HazardBlockComponent } from '../components/HazardBlockComponent';
import { IceBlockComponent } from '../components/IceBlockComponent';
import { KappaBlockComponent } from '../components/KappaBlockComponent';
import { KettleBlockComponent } from '../components/KettleBlockComponent';
import { LockBlockComponent } from '../components/LockBlockComponent';
import { MiniFuguBlockComponent } from '../components/MiniFuguBlockComponent';
import { PantherBlockComponent } from '../components/PantherBlockComponent';
import { PipeBlockComponent } from '../components/PipeBlockComponent';
import { PositionComponent } from '../components/PositionComponent';
import { NestBlockComponent } from '../components/NestBlockComponent';
import { RocketBlockComponent } from '../components/RocketBlockComponent';
import { ShellBlockComponent } from '../components/ShellBlockComponent';
import { SirenBlockComponent } from '../components/SirenBlockComponent';
import { SkullBlockComponent } from '../components/SkullBlockComponent';
import { SlimeBlockComponent } from '../components/SlimeBlockComponent';
import { SpiderBlockComponent } from '../components/SpiderBlockComponent';
import { StatueBlockComponent } from '../components/StatueBlockComponent';
import { ThickIceBlockComponent } from '../components/ThickIceBlockComponent';
import { ThrowerBlockComponent } from '../components/ThrowerBlockComponent';
import { VirusBlockComponent } from '../components/VirusBlockComponent';
import { WormBlockComponent } from '../components/WormBlockComponent';
import { ZombieBlockComponent } from '../components/ZombieBlockComponent';
import { ChocolateEggBlockComponent } from '../components/ChocolateEggBlockComponent';
import { FossilBlockComponent } from '../components/FossilBlockComponent';
import { CornBlockComponent } from '../components/CornBlockComponent';
import { PopcornBlockComponent } from '../components/PopcornBlockComponent';
import { TunaCanBlockComponent } from '../components/TunaCanBlockComponent';
import { TunaFishBlockComponent } from '../components/TunaFishBlockComponent';
import { RainbowBubbleBlockComponent } from '../components/RainbowBubbleBlockComponent';
import { CoolerBlockComponent } from '../components/CoolerBlockComponent';
import { TvBlockComponent } from '../components/TvBlockComponent';
import { ShieldBubbleBlockComponent } from '../components/ShieldBubbleBlockComponent';
import { BathtubBlockComponent } from '../components/BathtubBlockComponent';
import type {
  AndroidBlockProps,
  AnimalBlockProps,
  BasicBlockProps,
  BlockId,
  BlockProps,
  BlockType,
  BombBlockProps,
  ButterflyBlockProps,
  CageBlockProps,
  CandleBlockProps,
  CandyBagBlockProps,
  CandyBlockProps,
  ChainBlockProps,
  ChameleonBlockProps,
  CocoonBlockProps,
  CoffinBlockProps,
  CubeBlockProps,
  CurtainBlockProps,
  PondBlockProps,
  DogBlockProps,
  DoorBlockProps,
  FireboxBlockProps,
  FuguBlockProps,
  FullIceBlockProps,
  HalfIceBlockProps,
  HatBlockProps,
  HazardBlockProps,
  IceBlockProps,
  KappaBlockProps,
  KettleBlockProps,
  LockBlockProps,
  MiniFuguBlockProps,
  PantherBlockProps,
  PipeBlockProps,
  NestBlockProps,
  RocketBlockProps,
  ShellBlockProps,
  SirenBlockProps,
  SkullBlockProps,
  SlimeBlockProps,
  SpiderBlockProps,
  StatueBlockProps,
  ThickIceBlockProps,
  ThrowerBlockProps,
  VirusBlockProps,
  WallBlockProps,
  WormBlockProps,
  ZombieBlockProps,
  SodaBlockProps,
  MushroomBlockProps,
  AlarmBlockProps,
  BubblesBlockProps,
  ChocolateEggBlockProps,
  ParcelsBlockProps,
  FossilBlockProps,
  CornBlockProps,
  PopcornBlockProps,
  TunaCanBlockProps,
  TunaFishBlockProps,
  RainbowBubbleBlockProps,
  CoolerBlockProps,
  TvBlockProps,
  ShieldBubbleBlockProps,
  BathtubBlockProps,
  DirectionId,
  FirecrackerBoxBlockProps,
} from '../defs/block';
import { blockPropsMap } from '../defs/block';
import { BlockDef } from '../defs/map';
import type { GameScene } from '../GameScene';
import { SodaBlockComponent } from '../components/SodaBlockComponent';
import { MushroomBlockComponent } from '../components/MushroomBlockComponent';
import { AlarmBlockComponent } from '../components/AlarmBlockComponent';
import { BubblesBlockComponent } from '../components/BubblesBlockComponent';
import { ParcelsBlockComponent } from '../components/ParcelsBlockComponent';
import { WallBlockComponent } from '../components/WallBlockComponent';
import type { DespawnSource } from '../systems/DespawnSystem';
import {
  blockOptionToCountColor,
  blockOptionToCountDirection,
} from '../util/blockOption';
import { blockOptionToCountSize } from '../util/blockOption';
import { FirecrackerBoxBlockComponent } from '../components/FirecrackerBoxBlockComponent';
import { isDroppableCell } from '../util/mapTools';
import { getCollisionTracker } from '../util/CollisionTracker';
import { _spawnSharpenerBlockEntity } from '../blocks/sharpener/SharpenerBlockEntity';
import { _spawnBarrelBlockEntity } from '../blocks/barrel/BarrelBlockEntity';
import { _spawnRainbowSoapBlockEntity } from '../blocks/rainbowSoap/RainbowSoapBlockEntity';
import { _spawnBigEggBlockEntity } from '../blocks/bigEgg/BigEggBlockEntity';
import { _spawnPiggyBankBlockEntity } from '../blocks/piggyBank/PiggyBankBlockEntity';
import { _spawnBalloonBoxBlockEntity } from '../blocks/balloonBox/BalloonBoxBlockEntity';
import { _spawnOctopusBlockEntity } from '../blocks/octopus/OctopusBlockEntity';
import { _spawnFlowerFidgetBlockEntity } from '../blocks/flowerFidget/FlowerFidgetBlockEntity';
import { _spawnGrassBlockEntity } from '../blocks/grass/GrassBlockEntity';
import { _spawnMagicBunniesBlockEntity } from '../blocks/magicBunnies/MagicBunniesBlockEntity';
import { _spawnLaserBoxBlockEntity } from '../blocks/laserBox/LaserBoxBlockEntity';

// types
//-----------------------------------------------------------------------------
const BlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
);
export type BlockEntity = EntityType<typeof BlockEntityDef>;

const AndroidBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  AndroidBlockComponent,
);
export type AndroidBlockEntity = EntityType<typeof AndroidBlockEntityDef>;

const AnimalBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  AnimalBlockComponent,
);
export type AnimalBlockEntity = EntityType<typeof AnimalBlockEntityDef>;

const BasicBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  BasicBlockComponent,
);
export type BasicBlockEntity = EntityType<typeof BasicBlockEntityDef>;

const BombBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  BombBlockComponent,
  GlowComponent,
);
export type BombBlockEntity = EntityType<typeof BombBlockEntityDef>;

const CageBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CageBlockComponent,
);
export type CageBlockEntity = EntityType<typeof CageBlockEntityDef>;

const CandleBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CandleBlockComponent,
);
export type CandleBlockEntity = EntityType<typeof CandleBlockEntityDef>;

const ChainBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ChainBlockComponent,
);
export type ChainBlockEntity = EntityType<typeof ChainBlockEntityDef>;

const ChameleonBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ChameleonBlockComponent,
);
export type ChameleonBlockEntity = EntityType<typeof ChameleonBlockEntityDef>;

const CoffinBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CoffinBlockComponent,
);
export type CoffinBlockEntity = EntityType<typeof CoffinBlockEntityDef>;

const CubeBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CubeBlockComponent,
  GlowComponent,
);
export type CubeBlockEntity = EntityType<typeof CubeBlockEntityDef>;

const DogBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  DogBlockComponent,
);
export type DogBlockEntity = EntityType<typeof DogBlockEntityDef>;

const DoorBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  DoorBlockComponent,
);
export type DoorBlockEntity = EntityType<typeof DoorBlockEntityDef>;

const FireboxBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  FireboxBlockComponent,
);
export type FireboxBlockEntity = EntityType<typeof FireboxBlockEntityDef>;

const HatBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  HatBlockComponent,
);
export type HatBlockEntity = EntityType<typeof HatBlockEntityDef>;

const HazardBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  HazardBlockComponent,
);
export type HazardBlockEntity = EntityType<typeof HazardBlockEntityDef>;

const KettleBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  KettleBlockComponent,
);
export type KettleBlockEntity = EntityType<typeof KettleBlockEntityDef>;

const LockBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  LockBlockComponent,
);
export type LockBlockEntity = EntityType<typeof LockBlockEntityDef>;

const PantherBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  PantherBlockComponent,
);
export type PantherBlockEntity = EntityType<typeof PantherBlockEntityDef>;

const PipeBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  PipeBlockComponent,
);
export type PipeBlockEntity = EntityType<typeof PipeBlockEntityDef>;

const RocketBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  RocketBlockComponent,
  GlowComponent,
);
export type RocketBlockEntity = EntityType<typeof RocketBlockEntityDef>;

const SirenBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  SirenBlockComponent,
);
export type SirenBlockEntity = EntityType<typeof SirenBlockEntityDef>;

const SkullBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  SkullBlockComponent,
);
export type SkullBlockEntity = EntityType<typeof SkullBlockEntityDef>;

const FuguBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  FuguBlockComponent,
);
export type FuguBlockEntity = EntityType<typeof FuguBlockEntityDef>;

const MiniFuguBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  MiniFuguBlockComponent,
);
export type MiniFuguBlockEntity = EntityType<typeof MiniFuguBlockEntityDef>;

const KappaBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  KappaBlockComponent,
);
export type KappaBlockEntity = EntityType<typeof KappaBlockEntityDef>;

const SlimeBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  SlimeBlockComponent,
);
export type SlimeBlockEntity = EntityType<typeof SlimeBlockEntityDef>;

const SpiderBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  SpiderBlockComponent,
);
export type SpiderBlockEntity = EntityType<typeof SpiderBlockEntityDef>;

const StatueBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  StatueBlockComponent,
);
export type StatueBlockEntity = EntityType<typeof StatueBlockEntityDef>;

const ThrowerBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ThrowerBlockComponent,
);
export type ThrowerBlockEntity = EntityType<typeof ThrowerBlockEntityDef>;

const VirusBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  VirusBlockComponent,
);
export type VirusBlockEntity = EntityType<typeof VirusBlockEntityDef>;

const WallBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  WallBlockComponent,
);
export type WallBlockEntity = EntityType<typeof WallBlockEntityDef>;

const ZombieBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ZombieBlockComponent,
);
export type ZombieBlockEntity = EntityType<typeof ZombieBlockEntityDef>;

const IceBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  IceBlockComponent,
);
export type IceBlockEntity = EntityType<typeof IceBlockEntityDef>;

const ThickIceBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ThickIceBlockComponent,
);
export type ThickIceBlockEntity = EntityType<typeof ThickIceBlockEntityDef>;

const FullIceBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  FullIceBlockComponent,
);
export type FullIceBlockEntity = EntityType<typeof FullIceBlockEntityDef>;

const HalfIceBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  HalfIceBlockComponent,
);
export type HalfIceBlockEntity = EntityType<typeof HalfIceBlockEntityDef>;

const WormBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  WormBlockComponent,
);
export type WormBlockEntity = EntityType<typeof WormBlockEntityDef>;

const CocoonBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CocoonBlockComponent,
);
export type CocoonBlockEntity = EntityType<typeof CocoonBlockEntityDef>;

const ButterflyBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ButterflyBlockComponent,
);
export type ButterflyBlockEntity = EntityType<typeof ButterflyBlockEntityDef>;

const CurtainBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CurtainBlockComponent,
);
export type CurtainBlockEntity = EntityType<typeof CurtainBlockEntityDef>;

const PondBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  PondBlockComponent,
);
export type PondBlockEntity = EntityType<typeof PondBlockEntityDef>;

const CandyBagBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CandyBagBlockComponent,
);
export type CandyBagBlockEntity = EntityType<typeof CandyBagBlockEntityDef>;

const CandyBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CandyBlockComponent,
);
export type CandyBlockEntity = EntityType<typeof CandyBlockEntityDef>;

const ShellBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ShellBlockComponent,
);
export type ShellBlockEntity = EntityType<typeof ShellBlockEntityDef>;

const SodaBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  SodaBlockComponent,
);
export type SodaBlockEntity = EntityType<typeof SodaBlockEntityDef>;

const BubblesBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  BubblesBlockComponent,
);
export type BubblesBlockEntity = EntityType<typeof BubblesBlockEntityDef>;

const AlarmBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  AlarmBlockComponent,
);
export type AlarmBlockEntity = EntityType<typeof AlarmBlockEntityDef>;

const ChocolateEggBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ChocolateEggBlockComponent,
);
export type ChocolateEggBlockEntity = EntityType<
  typeof ChocolateEggBlockEntityDef
>;

const NestBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  NestBlockComponent,
);
export type NestBlockEntity = EntityType<typeof NestBlockEntityDef>;

const MushroomBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  MushroomBlockComponent,
);
export type MushroomBlockEntity = EntityType<typeof MushroomBlockEntityDef>;

const ParcelsBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ParcelsBlockComponent,
);
export type ParcelsBlockEntity = EntityType<typeof ParcelsBlockEntityDef>;

const FossilBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  FossilBlockComponent,
);
export type FossilBlockEntity = EntityType<typeof FossilBlockEntityDef>;

const CornBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CornBlockComponent,
);
export type CornBlockEntity = EntityType<typeof CornBlockEntityDef>;

const PopcornBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  PopcornBlockComponent,
);
export type PopcornBlockEntity = EntityType<typeof PopcornBlockEntityDef>;

const TunaCanBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  TunaCanBlockComponent,
);
export type TunaCanBlockEntity = EntityType<typeof TunaCanBlockEntityDef>;

const TunaFishBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  TunaFishBlockComponent,
);
export type TunaFishBlockEntity = EntityType<typeof TunaFishBlockEntityDef>;

const RainbowBubbleBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  RainbowBubbleBlockComponent,
);
export type RainbowBubbleBlockEntity = EntityType<
  typeof RainbowBubbleBlockEntityDef
>;

const FirecrackerBoxBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  FirecrackerBoxBlockComponent,
);
export type FirecrackerBoxBlockEntity = EntityType<
  typeof FirecrackerBoxBlockEntityDef
>;

const CoolerBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  CoolerBlockComponent,
);
export type CoolerBlockEntity = EntityType<typeof CoolerBlockEntityDef>;

const TvBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  TvBlockComponent,
);
export type TvBlockEntity = EntityType<typeof TvBlockEntityDef>;

const ShieldBubbleBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  ShieldBubbleBlockComponent,
);
export type ShieldBubbleBlockEntity = EntityType<
  typeof ShieldBubbleBlockEntityDef
>;

const BathtubBlockEntityDef = DefineEntity(
  Entity2D,
  PositionComponent,
  BlockComponent,
  BathtubBlockComponent,
);
export type BathtubBlockEntity = EntityType<typeof BathtubBlockEntityDef>;

export type PowerBlockEntity =
  | BombBlockEntity
  | CubeBlockEntity
  | RocketBlockEntity;

// constants
//-----------------------------------------------------------------------------
const spawnMap: Record<BlockType, (...args: any[]) => BlockEntity> = {
  android: _spawnAndroidBlockEntity,
  animal: _spawnAnimalBlockEntity,
  basic: _spawnBasicBlockEntity,
  bomb: _spawnBombBlockEntity,
  cage: _spawnCageBlockEntity,
  candle: _spawnCandleBlockEntity,
  chain: _spawnChainBlockEntity,
  chameleon: _spawnChameleonBlockEntity,
  coffin: _spawnCoffinBlockEntity,
  cube: _spawnCubeBlockEntity,
  dog: _spawnDogBlockEntity,
  door: _spawnDoorBlockEntity,
  firebox: _spawnFireboxBlockEntity,
  hat: _spawnHatBlockEntity,
  hazard: _spawnHazardBlockEntity,
  kappa: _spawnKappaBlockEntity,
  kettle: _spawnKettleBlockEntity,
  lock: _spawnLockBlockEntity,
  miniFugu: _spawnMiniFuguBlockEntity,
  panther: _spawnPantherBlockEntity,
  pipe: _spawnPipeBlockEntity,
  randomSpawn: _spawnRandomSpawnBlockEntity,
  rocket: _spawnRocketBlockEntity,
  siren: _spawnSirenBlockEntity,
  skull: _spawnSkullBlockEntity,
  slime: _spawnSlimeBlockEntity,
  spider: _spawnSpiderBlockEntity,
  fugu: _spawnFuguBlockEntity,
  statue: _spawnStatueBlockEntity,
  thrower: _spawnThrowerBlockEntity,
  virus: _spawnVirusBlockEntity,
  wall: _spawnWallBlockEntity,
  zombie: _spawnZombieBlockEntity,
  ice: _spawnIceBlockEntity,
  thickIce: _spawnThickIceBlockEntity,
  fullIce: _spawnFullIceBlockEntity,
  halfIce: _spawnHalfIceBlockEntity,
  worm: _spawnWormBlockEntity,
  cocoon: _spawnCocoonBlockEntity,
  butterfly: _spawnButterflyBlockEntity,
  curtain: _spawnCurtainBlockEntity,
  pond: _spawnPondBlockEntity,
  candyBag: _spawnCandyBagBlockEntity,
  candy: _spawnCandyBlockEntity,
  shell: _spawnShellBlockEntity,
  soda: _spawnSodaBlockEntity,
  bubbles: _spawnBubblesBlockEntity,
  alarm: _spawnAlarmBlockEntity,
  chocolateEgg: _spawnChocolateEggBlockEntity,
  nest: _spawnNestBlockEntity,
  mushroom: _spawnMushroomBlockEntity,
  parcels: _spawnParcelsBlockEntity,
  fossil: _spawnFossilBlockEntity,
  corn: _spawnCornBlockEntity,
  popcorn: _spawnPopcornBlockEntity,
  tunaCan: _spawnTunaCanBlockEntity,
  tunaFish: _spawnTunaFishBlockEntity,
  rainbowBubble: _spawnRainbowBubbleBlockEntity,
  firecrackerBox: _spawnFirecrackerBoxBlockEntity,
  cooler: _spawnCoolerBlockEntity,
  tv: _spawnTvBlockEntity,
  shieldBubble: _spawnShieldBubbleBlockEntity,
  bathtub: _spawnBathtubBlockEntity,
  sharpener: _spawnSharpenerBlockEntity,
  barrel: _spawnBarrelBlockEntity,
  rainbowSoap: _spawnRainbowSoapBlockEntity,
  bigEgg: _spawnBigEggBlockEntity,
  piggyBank: _spawnPiggyBankBlockEntity,
  balloonBox: _spawnBalloonBoxBlockEntity,
  octopus: _spawnOctopusBlockEntity,
  flowerFidget: _spawnFlowerFidgetBlockEntity,
  grass: _spawnGrassBlockEntity,
  magicBunnies: _spawnMagicBunniesBlockEntity,
  laserBox: _spawnLaserBoxBlockEntity,
};

// api
//-----------------------------------------------------------------------------
export function spawnBlockEntity(
  scene: GameScene,
  def: BlockDef,
  position: Vector2,
): BlockEntity {
  const props = blockPropsMap[def.id];
  const entity = spawnMap[props.type](scene, def, props, position);

  // Ensure visible when spawned, in case it was previously despawned
  entity.c.view2d.view.visible = true;

  return entity;
}

export function despawnBlockEntity(
  scene: GameScene,
  entity: BlockEntity,
  ignoreDestroy = false,
) {
  // if spawned
  if (entity.scene) {
    // Check if the cell of the entity was immobile before despawning.
    const cell = scene.sessionEntity.c.map.getCellAt(
      entity.c.position.mapPosition,
    );
    const wasImmobilized = cell && !isDroppableCell(scene, cell);

    // shutdown the glow effect, if any, so its particle emitter is released
    (entity.c as { glow?: GlowComponent }).glow?.shutdown();

    // unplace block entity from current position
    unplaceBlockEntity(scene, entity);

    // remove from scene
    scene.removeFromScene(entity);

    // TODO: this is a hack to fix a bug where the block entity is still visible after being removed
    // Somehow the same entity can be added to the scene again after removed, might be related to
    // entity queries and the way View2DSystem handles them
    entity.c.view2d.view.visible = false;

    // mark despawned. itd be nice if .removeFromScene did this /shrug
    entity.scene = undefined;

    // notify
    if (!ignoreDestroy) {
      scene.events.publish({ id: 'destroy', blockId: entity.c.block.blockId });
      scene.recordBlockBroken(entity.c.block.blockId);
    }

    // If the block was immobile, we should handle the bottom row to resolve any possible effects of the block becoming mobile.
    if (wasImmobilized) {
      resolveBottomRow(scene, entity);
    }
  }
}

/**
 * Check if the block was immobile and now can be moved.
 * Should only do this for the bottom row, as the top rows will be handled by the fall system.
 * @param scene
 * @param entity
 * @returns
 */
function resolveBottomRow(scene: GameScene, entity: BlockEntity) {
  // Block is not at the bottom row, bail.
  const map = scene.sessionEntity.c.map;
  const position = entity.c.position.mapPosition;
  const underPosition = position.clone();
  underPosition.y += 1;
  const underCell = map.getCellAt(underPosition);
  if (underCell && underCell.enabled) return;

  // No cell at position, bail.
  const cell = map.getCellAt(position);
  if (!cell) return;

  // Cell is still immobile, bail.
  if (!isDroppableCell(scene, cell)) return;

  // Trigger a fall collision to resolve any possible effects of the block becoming mobile.
  const collisionTracker = getCollisionTracker();
  collisionTracker.collideAt(map, position, 'fall');
}

/**
 * Mark this block to be despawned asynchronously, going through the DespawnSystem.
 * This can be used in place of despawnBlockEntity() if the despawn could be intercepted and animated by other systems.
 */
export async function despawnBlockEntityAsync(
  entity: BlockEntity,
  despawnSource: DespawnSource,
) {
  // Inform the despawn source has been set, so the DespawnSystem can start despawning it straight away
  entity.setModified(BlockComponent, { despawnSource });

  // The promise should be set by the DespawnSystem already
  if (entity.c.block.asyncDespawnPromise) {
    await entity.c.block.asyncDespawnPromise;
  }
}

export function setBlockEntityViewLayer(
  scene: GameScene,
  entity: BlockEntity | Entity2D,
  layer: 'default' | 'middle' | 'front' | 'back' | 'foreground',
) {
  scene.getSystem(View2DSystem)?.setEntityLayer(entity, layer);
}

export function placeBlockEntity(
  scene: GameScene,
  entity: BlockEntity,
  position: Vector2,
) {
  // add to map
  scene.sessionEntity.c.map.setBlockEntity(entity, position);

  // update block position
  entity.c.position.mapPosition = position;
}

export function unplaceBlockEntity(scene: GameScene, entity: BlockEntity) {
  // remove from map
  scene.sessionEntity.c.map.unsetBlockEntity(entity);
}

export function moveBlockEntity(
  scene: GameScene,
  entity: BlockEntity,
  position: Vector2,
) {
  // unplace block entity from current position
  unplaceBlockEntity(scene, entity);

  // place block entity at new position
  placeBlockEntity(scene, entity, position);
}

export function swapBlockEntities(
  scene: GameScene,
  entity1: BlockEntity,
  entity2: BlockEntity,
) {
  const position1 = entity1.c.position.mapPosition;
  const position2 = entity2.c.position.mapPosition;

  // unplace entity 1
  unplaceBlockEntity(scene, entity1);

  // move entity 2 to entity 1 position
  moveBlockEntity(scene, entity2, position1);

  // place entity 1 at entity 2 position
  placeBlockEntity(scene, entity1, position2);
}

// private: spawn
//-----------------------------------------------------------------------------
export function _spawnBaseBlockEntity<E extends BlockEntity>(
  Class: EntityClass<[], [], {}, E>,
  scene: GameScene,
  id: BlockId,
  props: BlockProps,
  position: Vector2,
  data?: KeyByComponent<E['c']>,
): E {
  // create entity
  const entity = createEntity(Class, {
    view2d: { view: new Container(), layer: 'default' },
    block: { id, props },
    position: { position },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...(data as any),
  });

  // place block entity
  placeBlockEntity(scene, entity, position);
  // add to scene
  scene.addToScene(entity);

  return entity;
}

function _spawnAndroidBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): AndroidBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    AndroidBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockAndroid: {
        props: props as AndroidBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockAndroid.view);

  return entity;
}

function _spawnAnimalBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): AnimalBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    AnimalBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockAnimal: {
        props: props as AnimalBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockAnimal.view);

  return entity;
}

function _spawnBasicBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): BasicBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    BasicBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockBasic: {
        props: props as BasicBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockBasic.view);

  return entity;
}

function _spawnBombBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): BombBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    BombBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockBomb: {
        props: props as BombBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.glow.view);
  entity.c.view2d.view.addChild(entity.c.blockBomb.view);

  return entity;
}

function _spawnCageBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CageBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CageBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCage: {
        props: props as CageBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCage.view);

  // Send the cage (bubble) to the foreground in new visuals
  setBlockEntityViewLayer(scene, entity, 'front');

  return entity;
}

function _spawnCandleBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CandleBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CandleBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCandle: {
        props: props as CandleBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCandle.view);

  return entity;
}

function _spawnChainBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ChainBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ChainBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockChain: {
        props: props as ChainBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockChain.view);

  return entity;
}

function _spawnChameleonBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ChameleonBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ChameleonBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockChameleon: {
        props: props as ChameleonBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockChameleon.view);

  return entity;
}

function _spawnCoffinBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CoffinBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CoffinBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCoffin: {
        props: props as CoffinBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCoffin.view);

  return entity;
}

function _spawnCubeBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CubeBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CubeBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCube: {
        props: props as CubeBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.glow.view);
  entity.c.view2d.view.addChild(entity.c.blockCube.view);

  return entity;
}

function _spawnDogBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): DogBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    DogBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockDog: {
        props: props as DogBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockDog.view);

  return entity;
}

function _spawnDoorBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): DoorBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    DoorBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockDoor: {
        props: props as DoorBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockDoor.view);

  return entity;
}

function _spawnFireboxBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): FireboxBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    FireboxBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockFirebox: {
        props: props as FireboxBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockFirebox.view);

  return entity;
}

function _spawnHatBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): HatBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    HatBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockHat: {
        props: props as HatBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockHat.view);

  return entity;
}

function _spawnHazardBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): HazardBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    HazardBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockHazard: {
        props: props as HazardBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockHazard.view);

  return entity;
}

function _spawnKettleBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): KettleBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    KettleBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockKettle: {
        props: props as KettleBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockKettle.view);

  return entity;
}

function _spawnLockBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): LockBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    LockBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockLock: {
        props: props as LockBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockLock.view);

  return entity;
}

function _spawnMiniFuguBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): MiniFuguBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    MiniFuguBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockMiniFugu: {
        props: props as MiniFuguBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockMiniFugu.view);

  return entity;
}

function _spawnPantherBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): PantherBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    PantherBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockPanther: {
        props: props as PantherBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockPanther.view);

  return entity;
}

function _spawnPipeBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): PipeBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    PipeBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockPipe: {
        props: props as PipeBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockPipe.view);

  return entity;
}

function _spawnRandomSpawnBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
) {
  // This is a special type of block that will spawn one of the blocks defined in the level data spawns
  // so in fact this block is never spawned, it just spawns another block instead
  const spawns = scene.getSpawnList();
  const id = randomItem(spawns);
  return spawnBlockEntity(scene, { id }, position);
}

function _spawnRocketBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): RocketBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    RocketBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockRocket: {
        props: props as RocketBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.glow.view);
  entity.c.view2d.view.addChild(entity.c.blockRocket.view);

  return entity;
}

function _spawnSirenBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): SirenBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    SirenBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockSiren: {
        props: props as SirenBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockSiren.view);

  return entity;
}

function _spawnSkullBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): SkullBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    SkullBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockSkull: {
        props: props as SkullBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockSkull.view);

  return entity;
}

function _spawnSlimeBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): SlimeBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    SlimeBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockSlime: {
        props: props as SlimeBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockSlime.view);

  return entity;
}

function _spawnSpiderBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): SpiderBlockEntity {
  const entity = _spawnBaseBlockEntity(
    SpiderBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockSpider: {
        props: props as SpiderBlockProps,
      },
    },
  );

  entity.c.view2d.view.addChild(entity.c.blockSpider.view);

  return entity;
}

function _spawnStatueBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): StatueBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    StatueBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockStatue: {
        props: props as StatueBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockStatue.view);

  return entity;
}

function _spawnFuguBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): FuguBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    FuguBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockFugu: {
        props: props as FuguBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockFugu.view);

  return entity;
}

function _spawnKappaBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): KappaBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    KappaBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockKappa: {
        props: props as KappaBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockKappa.view);

  return entity;
}

function _spawnThrowerBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ThrowerBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ThrowerBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockThrower: {
        props: props as ThrowerBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockThrower.view);

  return entity;
}

function _spawnVirusBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): VirusBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    VirusBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockVirus: {
        props: props as VirusBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockVirus.view);

  return entity;
}

function _spawnWallBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): WallBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    WallBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockWall: {
        props: props as WallBlockProps,
        count: def.option,
      },
    },
  );

  // build scene
  setBlockEntityViewLayer(scene, entity, 'front');
  entity.c.view2d.view.addChild(entity.c.blockWall.view);

  return entity;
}

function _spawnZombieBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ZombieBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ZombieBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockZombie: {
        props: props as ZombieBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockZombie.view);

  return entity;
}

function _spawnIceBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): IceBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    IceBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockIce: {
        props: props as IceBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockIce.view);

  return entity;
}

function _spawnThickIceBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ThickIceBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ThickIceBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockThickIce: {
        props: props as ThickIceBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockThickIce.view);

  return entity;
}

function _spawnFullIceBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): FullIceBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    FullIceBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockFullIce: {
        props: props as FullIceBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockFullIce.view);

  return entity;
}

function _spawnHalfIceBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): HalfIceBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    HalfIceBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockHalfIce: {
        props: props as HalfIceBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockHalfIce.view);

  return entity;
}

function _spawnWormBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): WormBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    WormBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockWorm: {
        props: props as WormBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockWorm.view);

  return entity;
}

function _spawnCocoonBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CocoonBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CocoonBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCocoon: {
        props: props as CocoonBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCocoon.view);

  return entity;
}

function _spawnButterflyBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ButterflyBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ButterflyBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockButterfly: {
        props: props as ButterflyBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockButterfly.view);

  return entity;
}

function _spawnCurtainBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CurtainBlockEntity {
  // Update props with current option, if is set
  if (def.option) {
    const countSize = blockOptionToCountSize(def.option);
    props = { ...props, ...countSize };
  }

  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CurtainBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCurtain: {
        props: props as CurtainBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCurtain.view);

  // Render the curtain above default-layer blocks across its full area.
  setBlockEntityViewLayer(scene, entity, 'middle');

  return entity;
}

function _spawnPondBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): PondBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    PondBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockPond: {
        props: props as PondBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockPond.view);

  return entity;
}

function _spawnCandyBagBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CandyBagBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CandyBagBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCandyBag: {
        props: props as CandyBagBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCandyBag.view);

  return entity;
}

function _spawnCandyBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CandyBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CandyBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCandy: {
        props: props as CandyBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCandy.view);

  return entity;
}

function _spawnShellBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ShellBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ShellBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockShell: {
        props: props as ShellBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockShell.view);

  return entity;
}

function _spawnSodaBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): SodaBlockEntity {
  const option = def.option ?? 0;
  const countDirection = blockOptionToCountDirection(option);

  // Soda bottles are a bit different from other entities: they are set from the editor based on one of the options
  // in their list, but when saved, they are saved as a count and direction. So we need to convert the option into a
  // count and direction here, if exists.
  if (!countDirection) {
    // If option is less than 3, then this is the original option from map definition
    const optionDef = props.options?.[def.option ?? 0];
    props = { ...props, ...optionDef } as SodaBlockProps;
  } else {
    // Otherwise, this is an option from saved game data, should be converted into count & driection
    const blockProps = blockPropsMap[def.id];

    // Find the list of options for this block
    const blockOptions = blockProps.options as {
      direction: DirectionId;
      width?: number;
      height?: number;
    }[];

    // Find the size of the block based on the direction
    const size = blockOptions.find((o) => {
      return o.direction === countDirection.direction;
    }) ?? { width: 1, height: 2 };

    // Update properties with size, count and direction
    props = { ...props, ...size, ...countDirection } as SodaBlockProps;
  }
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    SodaBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockSoda: {
        props,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockSoda.view);

  return entity;
}

function _spawnBubblesBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): BubblesBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    BubblesBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockBubbles: {
        props: props as BubblesBlockProps,
        amount: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockBubbles.view);

  return entity;
}

function _spawnAlarmBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): AlarmBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    AlarmBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockAlarm: {
        props: props as AlarmBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockAlarm.view);

  return entity;
}

function _spawnChocolateEggBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ChocolateEggBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ChocolateEggBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockChocolateEgg: {
        props: props as ChocolateEggBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockChocolateEgg.view);

  return entity;
}

function _spawnNestBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): NestBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    NestBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockNest: {
        props: props as NestBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockNest.view);

  return entity;
}

function _spawnMushroomBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): MushroomBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    MushroomBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockMushroom: {
        props: props as MushroomBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockMushroom.view);

  return entity;
}

function _spawnParcelsBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ParcelsBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ParcelsBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockParcels: {
        props: props as ParcelsBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockParcels.view);

  return entity;
}

function _spawnFossilBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): FossilBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    FossilBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockFossil: {
        props: props as FossilBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockFossil.view);

  return entity;
}

function _spawnCornBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CornBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CornBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCorn: {
        props: props as CornBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCorn.view);

  return entity;
}

function _spawnPopcornBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): PopcornBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    PopcornBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockPopcorn: {
        props: props as PopcornBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockPopcorn.view);

  return entity;
}

function _spawnTunaCanBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): TunaCanBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    TunaCanBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockTunaCan: {
        props: props as TunaCanBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockTunaCan.view);

  return entity;
}

function _spawnTunaFishBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): TunaFishBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    TunaFishBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockTunaFish: {
        props: props as TunaFishBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockTunaFish.view);

  return entity;
}

function _spawnRainbowBubbleBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): RainbowBubbleBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    RainbowBubbleBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockRainbowBubble: {
        props: props as RainbowBubbleBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockRainbowBubble.view);

  setBlockEntityViewLayer(scene, entity, 'front');

  return entity;
}

function _spawnFirecrackerBoxBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): FirecrackerBoxBlockEntity {
  // Update props with current option, if is set
  if (def.option) {
    const countColor = blockOptionToCountColor(def.option);
    props = { ...props, ...countColor };
  }

  // spawn base block
  const entity = _spawnBaseBlockEntity(
    FirecrackerBoxBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockFirecrackerBox: {
        props: props as FirecrackerBoxBlockProps,
      },
    },
  );

  entity.c.view2d.view.addChild(entity.c.blockFirecrackerBox.view);

  return entity;
}

function _spawnCoolerBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): CoolerBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    CoolerBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockCooler: {
        props: props as CoolerBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockCooler.view);

  return entity;
}

function _spawnTvBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): TvBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    TvBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockTv: {
        props: props as TvBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockTv.view);

  return entity;
}

function _spawnShieldBubbleBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): ShieldBubbleBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    ShieldBubbleBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockShieldBubble: {
        props: props as ShieldBubbleBlockProps,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockShieldBubble.view);

  setBlockEntityViewLayer(scene, entity, 'front');

  return entity;
}

function _spawnBathtubBlockEntity(
  scene: GameScene,
  def: BlockDef,
  props: BlockProps,
  position: Vector2,
): BathtubBlockEntity {
  // spawn base block
  const entity = _spawnBaseBlockEntity(
    BathtubBlockEntityDef,
    scene,
    def.id,
    props,
    position,
    {
      blockBathtub: {
        props: props as BathtubBlockProps,
        option: def.option,
      },
    },
  );

  // build scene
  entity.c.view2d.view.addChild(entity.c.blockBathtub.view);

  return entity;
}
